/**
 * Opslagkosten berekening utilities
 *
 * Berekent opslagkosten voor tapijtreiniging orders. Wanneer een kleed langer
 * dan de gratis periode (28 dagen / 4 weken) in de wasserij ligt, worden er
 * opslagkosten in rekening gebracht.
 *
 * Tarieven (in sync met Salesforce OpslagkostenService.cls):
 * - Gratis periode: 28 dagen (4 weken)
 * - Na gratis periode: €10 per begonnen week (incl. BTW)
 */

export const OPSLAGKOSTEN_CONFIG = {
  /** Aantal gratis dagen voordat opslagkosten ingaan (4 weken) */
  GRATIS_DAGEN: 28,

  /** Prijs per begonnen week in euro's (incl. 21% BTW) */
  PRIJS_PER_WEEK_INCL_BTW: 10,

  /** Prijs per begonnen week in euro's (excl. BTW) */
  PRIJS_PER_WEEK_EXCL_BTW: 8.26,
} as const

export interface OpslagkostenResult {
  /** Aantal begonnen weken na gratis periode */
  weken: number

  /** Totaal bedrag in euro's (incl. BTW) */
  bedrag: number

  /** True als de afleverdatum binnen de gratis periode valt */
  isGratis: boolean

  /** Aantal dagen tot einde gratis periode (negatief = al voorbij) */
  dagenTotOpslagkosten: number
}

function normalizeDate(date: string | Date | undefined | null): Date | null {
  if (!date) return null
  const parsed = date instanceof Date ? date : new Date(date)
  return isNaN(parsed.getTime()) ? null : parsed
}

function daysBetween(start: Date, end: Date): number {
  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate())
  return Math.floor((endDay.getTime() - startDay.getTime()) / (1000 * 60 * 60 * 24))
}

/**
 * Berekent opslagkosten voor een aflevermoment.
 *
 * @param startDatum - Moment dat aflevervoorstel is verstuurd (ISO string of Date)
 * @param afleverDatum - Geselecteerde aflevermoment (ISO string of Date)
 */
export function calculateOpslagkosten(
  startDatum: string | Date | undefined | null,
  afleverDatum: string | Date | undefined | null
): OpslagkostenResult {
  const start = normalizeDate(startDatum)
  const aflevering = normalizeDate(afleverDatum)

  if (!start || !aflevering) {
    return {
      weken: 0,
      bedrag: 0,
      isGratis: true,
      dagenTotOpslagkosten: OPSLAGKOSTEN_CONFIG.GRATIS_DAGEN,
    }
  }

  const dagen = daysBetween(start, aflevering)
  const dagenTotOpslagkosten = OPSLAGKOSTEN_CONFIG.GRATIS_DAGEN - dagen

  if (dagen <= OPSLAGKOSTEN_CONFIG.GRATIS_DAGEN) {
    return {
      weken: 0,
      bedrag: 0,
      isGratis: true,
      dagenTotOpslagkosten,
    }
  }

  const billableDagen = dagen - OPSLAGKOSTEN_CONFIG.GRATIS_DAGEN
  const weken = Math.ceil(billableDagen / 7)
  const bedrag = weken * OPSLAGKOSTEN_CONFIG.PRIJS_PER_WEEK_INCL_BTW

  return {
    weken,
    bedrag,
    isGratis: false,
    dagenTotOpslagkosten,
  }
}

export function formatOpslagkosten(bedrag: number): string {
  if (bedrag === 0) return 'Gratis'
  return `€${bedrag}`
}

export function getOpslagkostenLabel(result: OpslagkostenResult): string {
  if (result.isGratis) {
    return 'Geen opslagkosten'
  }
  const weekLabel = result.weken === 1 ? 'week' : 'weken'
  return `+€${result.bedrag} opslag (${result.weken} ${weekLabel})`
}
